import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import TabsSection from 'components/tabs/tabs-section/TabsSection';

import CampaignsAchievements from 'modules/organizations/components/campaign-achievements/CampaignsAchievements';
import CampaignsMessages from '../../CampaignsMessages';
import CampaignsFiltersMixed from '../campaigns-filters/CampaignsFiltersMixed';
import CampaignsCreateMixed from '../campaigns-create/CampaignsCreateMixed';
import CampaignsDashboardCcm from './ccm/CampaignsDashboardCcm';
import CampaignsDashboardCollab from './collab/CampaignsDashboardCollab';

import {
  fetchBrandCampaigns,
  fetchCampaignsCount,
  fetchCampaignsOverviews
} from 'modules/campaigns/reducers/brandCampaigns';
import {
  fetchOrganizationSettings
} from 'modules/organizations/reducers/organizationSettings';

import { __t, applyTranslations } from 'utils/language';
import { getBrandProfileId } from 'utils/organization';

import './campaigns-dashboard-mixed.scss';

applyTranslations(require('./campaigns-dashboard-mixed.i18n.json'));

import type {
  Campaign,
  CampaignCounts,
  CampaignQuery,
  CampaignType,
  Organization,
  OrganizationSettings,
  Pagination
} from 'types';

type OwnProps = {
  organization: Organization,
  shouldRenderBrandsCampaigns: boolean,
  shouldRenderCcmCampaigns: boolean
};

type StateProps = {
  organizationSettings: OrganizationSettings,
  organizationSettingsIsLoading: boolean,
  organizationsListIds: Array<number>,
  filter: string,
  campaigns: Array<Campaign>,
  campaignsCcm: Array<Campaign>,
  campaignsCount: CampaignCounts,
  campaignsCountCcm: CampaignCounts,
  campaignsPagination: Pagination,
  campaignsPaginationCcm: Pagination,
  campaignsIsLoading: boolean,
  campaignsIsLoadingCcm: boolean,
  campaignsCountsIsLoading: boolean,
  hasCampaigns: boolean,
  isCcmCreatorlessEnabled: boolean
};

type DispatchProps = {
  dispatchFetchCampaigns: (
    orgId: number,
    query: CampaignQuery
  ) => void,
  dispatchFetchCampaignsCount: (
    orgId: number,
    filter: string,
    search: string,
    campaignTypes: Array<CampaignType>
  ) => Promise<CampaignCounts>,
  dispatchFetchOrganizationSettings: (
    orgId: number
  ) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const MSG_KEY = 'campaigns_dashboard_mixed';
const NUM_CAMPAIGNS_WHEN_MIXED = 5;
const NUM_CAMPAIGNS_WHEN_SINGLE = 10;

const CampaignsDashboardMixed: React.FC<Props> = ({
  organization,
  organizationSettings,
  organizationSettingsIsLoading,
  organizationsListIds,
  filter,
  campaigns,
  campaignsCcm,
  campaignsCount,
  campaignsCountCcm,
  campaignsPagination,
  campaignsPaginationCcm,
  campaignsIsLoading,
  campaignsIsLoadingCcm,
  campaignsCountsIsLoading,
  hasCampaigns,
  shouldRenderBrandsCampaigns,
  shouldRenderCcmCampaigns,
  isCcmCreatorlessEnabled,
  dispatchFetchCampaigns,
  dispatchFetchCampaignsCount,
  dispatchFetchOrganizationSettings
}): JSX.Element => {
  const [page, setPage] = React.useState(1);
  const [pageCcm, setPageCcm] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [campaignState, setCampaignState] = React.useState('active');
  const [campaignTypeCheckboxes, setCampaignTypeCheckboxes] = React.useState([] as Array<CampaignType>);
  const brandProfileId = getBrandProfileId(organization);

  const getCampaignTypes = (): Array<CampaignType> => {
    const campaignTypes = [] as Array<CampaignType>;

    if (shouldRenderBrandsCampaigns) {
      campaignTypes.push('collab');
    }

    if (shouldRenderCcmCampaigns) {
      campaignTypes.push('ccm');
    }

    return !isEmpty(campaignTypeCheckboxes)
      ? intersection(campaignTypes, campaignTypeCheckboxes)
      : campaignTypes;
  };

  const getCampaignCount = (
    tab: string
  ): number => {
    const campaignTypes = getCampaignTypes();
    let count = 0;

    if (campaignTypes.includes('ccm')) {
      count += campaignsCountCcm[tab];
    }

    if (campaignTypes.includes('collab')) {
      count += campaignsCount[tab];
    }

    return count;
  };

  const handlePageChange = (
    campaignType: CampaignType,
    updatedPage: number
  ): void => {
    if (campaignType === 'ccm') {
      setPageCcm(updatedPage);
    } else {
      setPage(updatedPage);
    }
  };

  const handleSearchChange = (
    searchQuery: string
  ): void => {
    setPage(1);
    setPageCcm(1);
    setSearch(searchQuery);
  };

  const handleCheckboxChange = (
    campaignType: CampaignType
  ): void => {
    setPage(1);
    setPageCcm(1);

    if (campaignTypeCheckboxes.includes(campaignType)) {
      setCampaignTypeCheckboxes(campaignTypeCheckboxes
        .filter(c => c !== campaignType)
      );
    } else {
      setCampaignTypeCheckboxes([
        ...campaignTypeCheckboxes,
        campaignType
      ]);
    }
  };

  const handleTabChange = (
    campaignsState?: string | null
  ): void => {
    if (isNil(campaignState)) {
      return;
    }

    setPage(1);
    setPageCcm(1);
    setCampaignState(campaignsState as string);
  };

  const handleFetchCampaignsCount = (
    campaignTypes: Array<CampaignType>
  ): void => {
    dispatchFetchCampaignsCount(organization.id, filter, search, campaignTypes);
  };

  const handleFetchCampaigns = (
    campaignTypes: Array<CampaignType>,
    pageByCampaignType: number
  ): void => {
    const allowedCampaignTypes = getCampaignTypes();
    const updatedCampaignState = campaignState === 'preparation'
      ? `draft,${campaignState}`
      : campaignState;

    dispatchFetchCampaigns(organization.id, {
      filter,
      page: pageByCampaignType,
      search,
      state: updatedCampaignState,
      itemsPerPage: allowedCampaignTypes.length === 2
        ? NUM_CAMPAIGNS_WHEN_MIXED
        : NUM_CAMPAIGNS_WHEN_SINGLE,
      campaignTypes
    });
  };

  const renderFilters = (): React.ReactNode => (
    <React.Fragment>
      <div className="campaigns-dashboard-mixed__filters">
        <CampaignsFiltersMixed
          withCcm={shouldRenderCcmCampaigns}
          withBrand={shouldRenderBrandsCampaigns}
          search={search}
          campaignTypeCheckboxes={campaignTypeCheckboxes}
          onSearchChange={handleSearchChange}
          onCheckboxChange={handleCheckboxChange}
        />
      </div>
      <div className="campaigns-dashboard-mixed__create">
        <CampaignsCreateMixed
          brandProfileId={brandProfileId}
          organizationSettings={organizationSettings}
          hasCampaigns={hasCampaigns}
          withCcm={shouldRenderCcmCampaigns}
          withBrand={shouldRenderBrandsCampaigns}
          isCcmCreatorlessEnabled={isCcmCreatorlessEnabled}
        />
      </div>
    </React.Fragment>
  );

  const renderTabs = (): React.ReactNode => {
    const tabs = ['preparation', 'active', 'inactive']
      .map(tab => ({
        identifier: tab,
        label: `${__t(`${MSG_KEY}.tabs.${tab}`)} (${getCampaignCount(tab)})`,
        className: `campaigns-dashboard-mixed__tab--type-${tab}`,
        activeClassName: `campaigns-dashboard-mixed__tab--type-${tab}-active`
      }));

    return (
      <TabsSection
        items={tabs}
        activeTab={campaignState}
        onClick={handleTabChange}
      />
    );
  };

  const renderAchievements = (): React.ReactNode => (
    <div className="campaigns-dashboard-mixed__achievements">
      <div className="campaigns-dashboard-mixed__achievements-title">
        {__t(`${MSG_KEY}.achievements`)}
      </div>
      <CampaignsAchievements
        organizationId={organization.id}
      />
    </div>
  );

  const renderCampaigns = (): React.ReactNode => {
    const campaignTypes = getCampaignTypes();
    const isMixedCampaigns = shouldRenderBrandsCampaigns && shouldRenderCcmCampaigns;

    const containerClasses = classnames('campaigns-dashboard-mixed__campaigns', {
      'campaigns-dashboard-mixed__campaigns--mixed': isMixedCampaigns
    });

    return (
      <div className={containerClasses}>
        {
          shouldRenderCcmCampaigns && campaignTypes.includes('ccm') && (
            <CampaignsDashboardCcm
              campaigns={campaignsCcm}
              campaignsPagination={campaignsPaginationCcm}
              campaignsIsLoading={campaignsIsLoadingCcm || campaignsCountsIsLoading}
              campaignState={campaignState}
              isMixedCampaigns={isMixedCampaigns}
              hasFilters={Boolean(search)}
              onPageChange={updatedPage => handlePageChange('ccm', updatedPage)}
            />
          )
        }
        {
          shouldRenderBrandsCampaigns && campaignTypes.includes('collab') && (
            <CampaignsDashboardCollab
              campaigns={campaigns}
              campaignsPagination={campaignsPagination}
              campaignsIsLoading={campaignsIsLoading || campaignsCountsIsLoading}
              campaignState={campaignState}
              isMixedCampaigns={isMixedCampaigns}
              organizationsListIds={organizationsListIds}
              hasFilters={Boolean(search)}
              onPageChange={updatedPage => handlePageChange('collab', updatedPage)}
            />
          )
        }
      </div>
    );
  };

  const allowedCampaignTypes = getCampaignTypes();

  React.useEffect(() => {
    if (allowedCampaignTypes.includes('collab')) {
      handleFetchCampaignsCount(['collab']);
    }

    if (allowedCampaignTypes.includes('ccm')) {
      if (!isCcmCreatorlessEnabled) {
        handleFetchCampaigns(['ccm'], pageCcm);
      } else {
        handleFetchCampaignsCount(['ccm', 'ccm_creatorless']);
      }
    }
  }, [filter, search, campaignState, campaignTypeCheckboxes, isCcmCreatorlessEnabled]);

  React.useEffect(() => {
    if (allowedCampaignTypes.includes('ccm')) {
      if (!isCcmCreatorlessEnabled) {
        handleFetchCampaigns(['ccm'], pageCcm);
      } else {
        handleFetchCampaigns(['ccm', 'ccm_creatorless'], pageCcm);
      }
    }
  }, [filter, pageCcm, search, campaignState, campaignTypeCheckboxes, isCcmCreatorlessEnabled]);

  React.useEffect(() => {
    if (allowedCampaignTypes.includes('collab')) {
      handleFetchCampaigns(['collab'], page);
    }
  }, [filter, page, search, campaignState, campaignTypeCheckboxes]);

  React.useEffect(() => {
    dispatchFetchOrganizationSettings(organization.id);
  }, [organization.id]);

  return (
    <div className="campaigns-dashboard-mixed">
      {
        !organizationSettingsIsLoading && (
          <CampaignsMessages
            organization={organization}
            hasCampaigns={hasCampaigns}
            organizationSettings={organizationSettings}
          />
        )
      }
      <div className="campaigns-dashboard-mixed__header">
        <div className="campaigns-dashboard-mixed__title">
          {__t(`${MSG_KEY}.title`)}
        </div>
        {
          !organizationSettingsIsLoading &&
            renderFilters()
        }
      </div>
      <div className="campaigns-dashboard-mixed__tabs">
        {renderTabs()}
      </div>
      <div className="campaigns-dashboard-mixed__list">
        {renderCampaigns()}
      </div>
      {
        shouldRenderBrandsCampaigns && allowedCampaignTypes.includes('collab') &&
          renderAchievements()
      }
    </div>
  );
};

const mapStateToProps = ({
  brandCampaigns,
  brandsFilter,
  organizations,
  organizationSettings,
  runtimeConfig
}): StateProps => ({
  organizationSettings: organizationSettings.organization,
  organizationSettingsIsLoading: organizationSettings.isLoading,
  organizationsListIds: organizations.list.map(organization => organization.id),
  filter: brandsFilter.activeFilter,
  campaigns: brandCampaigns.list,
  campaignsCcm: brandCampaigns.listCcm,
  campaignsCount: brandCampaigns.totalCount,
  campaignsCountCcm: brandCampaigns.totalCountCcm,
  campaignsPagination: brandCampaigns.pagination,
  campaignsPaginationCcm: brandCampaigns.paginationCcm,
  campaignsIsLoading: brandCampaigns.isLoading,
  campaignsIsLoadingCcm: brandCampaigns.isLoadingCcm,
  campaignsCountsIsLoading: brandCampaigns.isLoadingCounts,
  hasCampaigns: brandCampaigns.hasCampaigns,
  isCcmCreatorlessEnabled: Boolean(runtimeConfig.config.isCcmEnabled)
    && Boolean(runtimeConfig.config.isCcmCreatorlessEnabled)
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaigns: (orgId, query) => (
    dispatch(fetchBrandCampaigns(orgId, query))
      .then(campaigns => (
        dispatch(fetchCampaignsOverviews(campaigns.data.map((c: Campaign) => c.id)))
      ))
  ),
  dispatchFetchCampaignsCount: (orgId, filter, search, campaignTypes) => (
    dispatch(fetchCampaignsCount(orgId, filter, search, campaignTypes))
  ),
  dispatchFetchOrganizationSettings: (orgId) => (
    dispatch(fetchOrganizationSettings(orgId))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignsDashboardMixed);
